import { repeatIndex } from "@@/utilities/repeat";
import { defaultBookableSettings } from "../../acquire/bookable-settings";
import { isQuantityPickerBookableOptionValue } from "../../acquire/resource-reference-options/quantity-picker-bookable-option-value";
import { consolidateResourceRequestsOption } from "../../acquire/resources/resource-helper";
import type { ResourceReservation } from "../../acquire/resources/resource-reservation";

import { calculateDiscountedPriceOnOptionValue } from "@@/shared/discounts/discount-types/discount-calculator";
import { zeroPrice } from "../../prices/price";
import { sumPrices } from "../../prices/sum-prices";
import type { OrderV1 } from "../order";
import { isOrderItemBookable_V2, isOrderItem_V2 } from "./is-order-item-v2";
import { calculateOrderItemPriceWithoutOptionals } from "./order-item-calculations";
import type {
    OrderItemId,
    OrderItemId_V2} from "./order-item-id";
import {
    orderItemIdZodSchema_V2,
} from "./order-item-id";
import type { OrderItem_V1 } from "./order-item-v1";
import type {
    OrderItem_V2} from "./order-item-v2";
import {
    orderItemBookableZodSchema_V2,
    orderItemBuyableZodSchema_V2,
    orderItemGiftCardZodSchema_V2,
    orderItemOptionalFactory_V2,
    orderItemReservationFactory_V2,
    orderItemVoucherZodSchema_V2,
} from "./order-item-v2";

const migrateOrderItemId = (oldOrderItem: OrderItem_V1): OrderItemId => {
    const oldId = oldOrderItem._id;
    if (oldId.startsWith("order_item_v2__"))
        return oldId as unknown as OrderItemId_V2; // just a precaution
    const parsed = oldId.replace("order_item_", "");
    if (parsed.length < 10) {
        // Generate a new one, but in a good way
        // eslint-disable-next-line no-console
        console.warn("Weird order item id v1, " + oldOrderItem._id);
    }
    return orderItemIdZodSchema_V2.parse(`order_item_v2__${parsed}`);
};

const orderMigration = (
    order: OrderV1<OrderItem_V1 | OrderItem_V2>,
    resourceReservations: ResourceReservation[],
): OrderV1<OrderItem_V2> => {
    try {
        const items = order.orderItems.flatMap(oi => {
            if (isOrderItem_V2(oi)) return oi;
            switch (oi.acquireType) {
                case "BUYABLE": {
                    const pricePerItem =
                        calculateOrderItemPriceWithoutOptionals(oi);

                    const totalPriceWithoutOptional = sumPrices(
                        repeatIndex(oi.quantity.value).map(() => pricePerItem),
                    );

                    const item = {
                        ...oi,
                        _id: migrateOrderItemId(oi),
                        _type: "ORDER_ITEM_V2",
                        parentId: undefined,
                        pricePerItem: pricePerItem,
                        price: totalPriceWithoutOptional,
                        originalPricePerItem: pricePerItem,
                        originalPrice: totalPriceWithoutOptional,
                        availability: oi.availability
                            ? oi.availability
                            : undefined,
                        articleNumber: oi.articleNumber
                            ? oi.articleNumber
                            : undefined,
                        discounts: oi.discounts ?? [],
                    };

                    const parsedItem = orderItemBuyableZodSchema_V2.parse(item);

                    const pickedOptionals = oi.pickedOptionals.map(
                        pickedOption =>
                            orderItemOptionalFactory_V2({
                                pickedOption,
                                discounts: [],
                                messageToProvider: undefined,
                                productPricePerItem: pricePerItem,
                                productPrice: totalPriceWithoutOptional,
                                quantity: parsedItem.quantity,
                                parent: parsedItem,
                            }),
                    );

                    return [parsedItem, ...pickedOptionals];
                }
                case "VOUCHER": {
                    const pricePerItem =
                        calculateOrderItemPriceWithoutOptionals(oi);

                    const totalPriceWithoutOptional = sumPrices(
                        repeatIndex(oi.quantity.value).map(() => pricePerItem),
                    );

                    const item = {
                        ...oi,
                        _id: migrateOrderItemId(oi),
                        _type: "ORDER_ITEM_V2",
                        parentId: undefined,
                        pricePerItem: pricePerItem,
                        price: totalPriceWithoutOptional,
                        originalPricePerItem: pricePerItem,
                        originalPrice: totalPriceWithoutOptional,

                        availability: oi.availability
                            ? oi.availability
                            : undefined,
                        articleNumber: oi.articleNumber
                            ? oi.articleNumber
                            : undefined,
                    };

                    const parsedItem = orderItemVoucherZodSchema_V2.parse(item);

                    const pickedOptionals = oi.pickedOptionals.map(
                        pickedOption =>
                            orderItemOptionalFactory_V2({
                                pickedOption,
                                discounts: [],
                                messageToProvider: undefined,
                                productPricePerItem: pricePerItem,
                                productPrice: totalPriceWithoutOptional,
                                quantity: parsedItem.quantity,
                                parent: parsedItem,
                            }),
                    );

                    return [parsedItem, ...pickedOptionals];
                }
                case "GIFTCARD": {
                    const pricePerItem =
                        calculateOrderItemPriceWithoutOptionals(oi);

                    const totalPriceWithoutOptional = sumPrices(
                        repeatIndex(oi.quantity.value).map(() => pricePerItem),
                    );

                    const item = {
                        ...oi,
                        _id: migrateOrderItemId(oi),
                        _type: "ORDER_ITEM_V2",
                        parentId: undefined,
                        pricePerItem: pricePerItem,
                        price: totalPriceWithoutOptional,
                        originalPricePerItem: pricePerItem,
                        originalPrice: totalPriceWithoutOptional,

                        availability: oi.availability
                            ? oi.availability
                            : undefined,
                        articleNumber: oi.articleNumber
                            ? oi.articleNumber
                            : undefined,
                    };

                    const parsedItem =
                        orderItemGiftCardZodSchema_V2.parse(item);

                    const pickedOptionals = oi.pickedOptionals.map(
                        pickedOption =>
                            orderItemOptionalFactory_V2({
                                pickedOption,
                                discounts: [],
                                messageToProvider: undefined,
                                productPricePerItem: pricePerItem,
                                productPrice: totalPriceWithoutOptional,
                                quantity: parsedItem.quantity,
                                parent: parsedItem,
                            }),
                    );

                    return [parsedItem, ...pickedOptionals];
                }
                case "BOOKABLE": {
                    const pricePerItem =
                        calculateOrderItemPriceWithoutOptionals(oi);

                    const totalPriceWithoutOptional = sumPrices(
                        repeatIndex(oi.quantity.value).map(() => pricePerItem),
                    );

                    if (!oi.time) return oi;

                    const discounts = oi.discounts.map(discount => {
                        if (discount.type === "TIME_PRICE_STEP_DISCOUNT") {
                            return {
                                ...discount,
                                validTimeRanges: discount.validTimeRanges
                                    ? discount.validTimeRanges
                                    : [],
                                steps: [
                                    ...discount.steps.map(step => {
                                        return {
                                            ...step,
                                            amountIncludingVat:
                                                step.amountIncludingVat ===
                                                undefined
                                                    ? (step.price
                                                          ?.amountIncludingVat ??
                                                      0)
                                                    : step.amountIncludingVat, //old TIME_PRICE_STEP_DISCOUNT discounts have price and no amountIncludingVat.
                                        };
                                    }),
                                ],
                            };
                        }
                        return discount;
                    });

                    const item = {
                        ...oi,
                        _id: migrateOrderItemId(oi),
                        _type: "ORDER_ITEM_V2",
                        parentId: undefined,
                        pricePerItem: zeroPrice,
                        price: zeroPrice,
                        originalPricePerItem: zeroPrice,
                        originalPrice: zeroPrice,
                        bookingType: oi.bookingType
                            ? oi.bookingType
                            : "GENERIC",

                        availability: oi.availability
                            ? oi.availability
                            : undefined,
                        articleNumber: oi.articleNumber
                            ? oi.articleNumber
                            : undefined,
                        hasAccess: !!oi.hasAccess,
                        settings: oi.settings
                            ? oi.settings
                            : defaultBookableSettings,
                        discounts,
                    };

                    const parsedItem =
                        orderItemBookableZodSchema_V2.parse(item);

                    const pickedOptionals = oi.pickedOptionals.map(
                        pickedOption =>
                            orderItemOptionalFactory_V2({
                                pickedOption,
                                discounts: [],
                                messageToProvider: undefined,
                                productPricePerItem: pricePerItem,
                                productPrice: totalPriceWithoutOptional,
                                quantity: parsedItem.quantity,
                                parent: parsedItem,
                            }),
                    );
                    const consolidated = consolidateResourceRequestsOption(
                        resourceReservations ?? [],
                    );
                    const mains = resourceReservations
                        .filter(r => r.main)
                        .flatMap(o => o.optionValues);

                    const reservations = consolidated.flatMap(value => {
                        if (isQuantityPickerBookableOptionValue(value)) {
                            if (!item.time) return [];
                            if (!isOrderItemBookable_V2(item)) return [];

                            const isMain = mains.some(o => o._id === value._id);
                            const newChildItem = orderItemReservationFactory_V2(
                                {
                                    value,
                                    discounts: [...discounts],
                                    messageToProvider: undefined,
                                    quantity: parsedItem.quantity,
                                    time: item.time,
                                    parent: item,
                                    main: isMain,
                                },
                            );

                            if (oi.discounts.length > 0) {
                                const price =
                                    calculateDiscountedPriceOnOptionValue(
                                        oi.discounts,
                                    )(item, value);
                                return {
                                    ...newChildItem,
                                    price: price.totalPrice,
                                    pricePerItem: price.pricePer,
                                };
                            }
                            return newChildItem;
                        }
                        return [];
                    });

                    return [parsedItem, ...pickedOptionals, ...reservations];
                }
            }
        });

        return { ...order, orderItems: items } as OrderV1;
    } catch (error) {
        // eslint-disable-next-line no-console
        console.error("UNABLE TO MIGRATE " + order._id, error);
        throw new Error("UNABLE TO MIGRATE " + order._id);
        // return order;
    }
};

export { orderMigration };
